import { SortDirection, TableSort } from "@redotech/redo-views-common/table";

export const SHORTAGE_COLUMN_KEY = "shortage";

/**
 * Units that must be built before every committed order can ship. Available is
 * physical only (on hand minus committed and unavailable), so a negative
 * available is exactly the amount owed.
 */
export function shortageFromAvailable(available: number): number {
  return Math.max(0, -available);
}

/**
 * Units a production run starts with for a given available quantity. A
 * negative available is the shortage owed to committed orders, so one run
 * clears it; anything else starts at one unit.
 */
export function initialProductionQuantity(available: number): number {
  const shortage = shortageFromAvailable(available);
  return shortage > 0 ? Math.ceil(shortage) : 1;
}

/**
 * Quantity a production run shows. The prefill covers the shortage at the
 * picked location, and a location the map does not list owes nothing there, so
 * it starts at one unit. The product-wide available stands in only when there
 * is no per-location data at all. A typed amount replaces the prefill, and the
 * result is clamped to what the run can build, so picking a location with more
 * capacity restores the request rather than ratcheting it down.
 */
export function deriveProductionQuantity<T>({
  availableByLocation,
  locationId,
  maxProducible,
  productAvailable,
  typedQuantity,
}: {
  availableByLocation: ReadonlyMap<T, number>;
  locationId: T | undefined;
  maxProducible: number;
  productAvailable: number;
  typedQuantity: number | null;
}): number {
  let available: number;
  if (availableByLocation.size === 0) {
    available = productAvailable;
  } else if (locationId === undefined) {
    available = 0;
  } else {
    available = availableByLocation.get(locationId) ?? 0;
  }
  const requested = typedQuantity ?? initialProductionQuantity(available);
  return Math.min(requested, maxProducible);
}

export interface InventoryLevelsSortRequest {
  sortBy: string | undefined;
  sortDirection: "asc" | "desc" | undefined;
}

/**
 * Shortage has no index field of its own: it is derived from available, so
 * sorting by shortage is sorting by available in the opposite direction.
 */
export function inventoryLevelsSortRequest(
  sort: TableSort | undefined,
): InventoryLevelsSortRequest {
  if (!sort) {
    return { sortBy: undefined, sortDirection: undefined };
  }

  const ascending = sort.direction === SortDirection.ASC;
  if (sort.key === SHORTAGE_COLUMN_KEY) {
    return {
      sortBy: "totalAvailable",
      sortDirection: ascending ? "desc" : "asc",
    };
  }

  return { sortBy: sort.key, sortDirection: ascending ? "asc" : "desc" };
}
